import { useState, useEffect, useCallback } from 'react';
import { formatVenueForDisplay } from '../../../constants/realVenueData';
import { getMetroDistanceText } from '../../../services/metroDistanceService';
import venueApiService from '../../../services/venueApi';
import { shouldShowVenuePricing, isSearchPriceFilterEnabled } from '../../../utils/venuePricing';
import { FILTER_OPTIONS } from '../constants';

// Helper function to calculate distance between two coordinates in miles
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    try {
      if (!navigator || !navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser.'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        (error) => reject(error),
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 300000
        }
      );
    } catch (error) {
      reject(error);
    }
  });
};

export const useSearchResults = (localSearchData, filters, aiVenuesFromState = null) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const priceFilter = isSearchPriceFilterEnabled() ? filters.price : null;

  const loadVenueData = useCallback(async () => {
    if (aiVenuesFromState && Array.isArray(aiVenuesFromState) && aiVenuesFromState.length > 0) {
      let filteredVenues = aiVenuesFromState;
      if (priceFilter) {
        const priceFilterDef = FILTER_OPTIONS.price.find((p) => p.value === priceFilter);
        if (priceFilterDef) {
          filteredVenues = filteredVenues.filter((venue) => {
            if (!shouldShowVenuePricing(venue)) return true;
            const hourlyRate = venue.hourly_rate || venue.hourlyRate || 0;
            if (priceFilterDef.max && priceFilterDef.min) {
              return hourlyRate >= priceFilterDef.min && hourlyRate < priceFilterDef.max;
            }
            if (priceFilterDef.max) return hourlyRate < priceFilterDef.max;
            if (priceFilterDef.min) return hourlyRate >= priceFilterDef.min;
            return true;
          });
        }
      }
      if (filters.attendees) {
        const attendeesFilter = FILTER_OPTIONS.attendees.find(a => a.value === filters.attendees);
        if (attendeesFilter) {
          filteredVenues = filteredVenues.filter(venue => {
            const maxCapacity = venue.capacity || venue.max_capacity || 0;
            if (attendeesFilter.max && attendeesFilter.min) return maxCapacity >= attendeesFilter.min && maxCapacity <= attendeesFilter.max;
            if (attendeesFilter.max) return maxCapacity <= attendeesFilter.max;
            if (attendeesFilter.min) return maxCapacity >= attendeesFilter.min;
            return true;
          });
        }
      }
      if (filters.vibe) {
        filteredVenues = filteredVenues.filter(venue => {
          const venueVibes = (venue.vibes || []).map(v => v.toLowerCase());
          return venueVibes.some(vibe => vibe === filters.vibe.toLowerCase());
        });
      }
      if (filters.eventType) {
        filteredVenues = filteredVenues.filter(venue => {
          const eventTypes = (venue.event_types || venue.eventTypes || []).map(et => et.toLowerCase());
          return eventTypes.includes(filters.eventType.toLowerCase());
        });
      }
      const formattedResults = filteredVenues.map(venue => {
        const formattedVenue = formatVenueForDisplay(venue);
        formattedVenue.distance = getMetroDistanceText(venue);
        return formattedVenue;
      });
      setResults(formattedResults);
      setLoading(false);
      return;
    }
    try {
      // Use filters.eventType if available, otherwise fall back to localSearchData.eventType
      const eventTypeToUse = filters.eventType || localSearchData.eventType;
      
      const apiSearchParams = {
        location: localSearchData.location,
        eventType: eventTypeToUse,
        priceRange: priceFilter ? (() => {
          const priceFilterDef = FILTER_OPTIONS.price.find(p => p.value === priceFilter);
          return priceFilterDef ? { min: priceFilterDef.min, max: priceFilterDef.max } : null;
        })() : null,
        vibe: filters.vibe
      };
      
      let filteredVenues = await venueApiService.searchVenues(apiSearchParams);
      
      // Handle "Current location" - get user's location and filter venues within 50 miles
      if (localSearchData.location && localSearchData.location.toLowerCase() === 'current location') {
        try {
          const position = await getCurrentPosition();
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          
          filteredVenues = filteredVenues.filter(venue => {
            const distance = calculateDistance(userLat, userLng, venue.coordinates.lat, venue.coordinates.lng);
            return distance <= 50;
          });
        } catch (error) {
          // Keep all venues if location fails
        }
      }
    
      // Apply client-side filters (always apply as fallback, even if sent to API)
      // This ensures filters work even if backend doesn't support them or has issues
      
      // Price filter - venues with hide_price appear in all price filter options
      if (priceFilter) {
        const priceFilterDef = FILTER_OPTIONS.price.find(p => p.value === priceFilter);
        if (priceFilterDef) {
          filteredVenues = filteredVenues.filter(venue => {
            if (!shouldShowVenuePricing(venue)) return true;
            const hourlyRate = venue.hourly_rate || venue.hourlyRate || 0;
            if (priceFilterDef.max && priceFilterDef.min) {
              return hourlyRate >= priceFilterDef.min && hourlyRate < priceFilterDef.max;
            } else if (priceFilterDef.max) {
              return hourlyRate < priceFilterDef.max;
            } else if (priceFilterDef.min) {
              return hourlyRate >= priceFilterDef.min;
            }
            return true;
          });
        }
      }
    
      // Attendees filter
      if (filters.attendees) {
        const attendeesFilter = FILTER_OPTIONS.attendees.find(a => a.value === filters.attendees);
        if (attendeesFilter) {
          filteredVenues = filteredVenues.filter(venue => {
            const maxCapacity = venue.capacity || 0;
            if (attendeesFilter.max && attendeesFilter.min) {
              return maxCapacity >= attendeesFilter.min && maxCapacity <= attendeesFilter.max;
            } else if (attendeesFilter.max) {
              return maxCapacity <= attendeesFilter.max;
            } else if (attendeesFilter.min) {
              return maxCapacity >= attendeesFilter.min;
            }
            return true;
          });
        }
      }
      
      // Vibe filter
      if (filters.vibe) {
        filteredVenues = filteredVenues.filter(venue => {
          const venueVibes = (venue.vibes || []).map(v => v.toLowerCase());
          const filterVibe = filters.vibe.toLowerCase();
          return venueVibes.some(vibe => vibe === filterVibe);
        });
      }
      
      // Event Type filter
      if (filters.eventType) {
        filteredVenues = filteredVenues.filter(venue => {
          const eventTypes = (venue.event_types || venue.eventTypes || []).map(et => et.toLowerCase());
          return eventTypes.includes(filters.eventType.toLowerCase());
        });
      }
      
      // Format the filtered results
      const formattedResults = filteredVenues.map(venue => {
        const formattedVenue = formatVenueForDisplay(venue);
        formattedVenue.distance = getMetroDistanceText(venue);
        return formattedVenue;
      });
      
      setResults(formattedResults);
      setLoading(false);
      
    } catch (error) {
      setResults([]);
      setLoading(false);
    }
  }, [localSearchData, filters, aiVenuesFromState, priceFilter]);

  useEffect(() => {
    loadVenueData();
  }, [loadVenueData]);

  return {
    results,
    loading,
    refetch: loadVenueData
  };
};

